import { icons, images, intl, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { useCallback, useState } from 'react'
import { YouTubeStreamDefinition, YouTubeStreamId } from './model'
import { YouTubeStreamForm } from './youtube-streaming-form'

const Container = styled(ui.FlexColumn)`
	row-gap: 20px;
	width: 100%;
`

const EmptyState = styled.div`
	margin-top: 24px;
	text-align: center;
`

const EmptyStateIcon = styled.img`
	width: 88px;
	height: 88px;
	margin-bottom: 16px;
`

const EmptyStateTitle = styled.div`
	font-size: 16px;
	line-height: 21px;
	font-weight: 500;
	margin-bottom: 6px;
`

const EmptyStateDescription = styled.div`
	font-size: 14px;
	line-height: 17px;
`

type Props = {
	youtubeStreams: Array<YouTubeStreamDefinition>
	onSelect: (youtubeId: YouTubeStreamId) => void
	onCreate: (definition: YouTubeStreamDefinition) => void
}

export const YouTubeStreamingList = ({
	youtubeStreams,
	onSelect,
	onCreate,
}: Props): JSX.Element => {
	const translate = intl.useTranslate()
	const [selected, setSelected] = useState<YouTubeStreamDefinition | null>(null)
	const [showForm, setShowForm] = useState(false)

	const handleCreate = useCallback(
		(definition: YouTubeStreamDefinition) => {
			onCreate(definition)
			setShowForm(false)
		},
		[onCreate]
	)

	return (
		<Container>
			{!showForm ? (
				<>
					<ui.ButtonLarge
						style={{
							width: '100%',
							alignSelf: 'center',
							justifySelf: 'center',
						}}
						icon={<icons.YouTubeStreamIcon />}
						onClick={() => setShowForm(true)}
					>
						<intl.Translate>room_menu.youtube_stream.new</intl.Translate>
					</ui.ButtonLarge>
					{youtubeStreams.length !== 0 ? (
						<>
							<ui.H3>
								<intl.Translate>
									room_menu.youtube_stream.list.header
								</intl.Translate>
							</ui.H3>
							<ui.CheckboxList>
								{youtubeStreams.map(youtubeStream => (
									<ui.CheckboxListItem
										key={youtubeStream.id}
										type="radio"
										name={youtubeStream.name}
										value={youtubeStream.id}
										checked={selected?.id === youtubeStream.id}
										imageSrc={images.youtubeTemplateIcon}
										onChange={checked => {
											if (checked) {
												setSelected(youtubeStream)
											}
										}}
									/>
								))}
							</ui.CheckboxList>
							{selected && (
								<ui.ButtonPrimary
									style={{ width: '200px' }}
									onClick={() => onSelect(selected.id)}
								>
									<intl.Translate>
										room_menu.youtube_stream.start_button
									</intl.Translate>
								</ui.ButtonPrimary>
							)}
						</>
					) : (
						<EmptyState>
							<EmptyStateIcon src={images.youtubeTemplateCircleIcon} alt="" />
							<EmptyStateTitle>
								{translate('room_menu.youtube_stream.empty.from_tools.title')}
							</EmptyStateTitle>
							<EmptyStateDescription>
								{translate(
									'room_menu.youtube_stream.empty.from_tools.description'
								)}
							</EmptyStateDescription>
						</EmptyState>
					)}
				</>
			) : (
				<YouTubeStreamForm onCreate={handleCreate} />
			)}
		</Container>
	)
}
